import { useEffect, useRef, useState } from "react";
import {
  Modal,
  Stack,
  Group,
  Button,
  TextInput,
  NumberInput,
  Textarea,
  ActionIcon,
  Image,
  Loader,
} from "@mantine/core";
import {
  Dropzone,
  FileWithPath,
  PDF_MIME_TYPE,
  IMAGE_MIME_TYPE,
} from "@mantine/dropzone";
import { MonthPickerInput } from "@mantine/dates";
import { useForm, isNotEmpty } from "@mantine/form";
import { ReactComponent as FileIcon } from "@/assets/icons/file.svg";
import { ReactComponent as EditIcon } from "@/assets/icons/edit.svg";
import { ReactComponent as UploadIcon } from "@/assets/icons/upload.svg";
import { ReactComponent as DeleteIcon } from "@/assets/icons/delete.svg";
import { IssueApi } from "@/api/issue.api";
import { FileApi } from "@/api/file.api";
import dayjs from "dayjs";
import "dayjs/locale/hr";
import {
  errorNotification,
  successNotification,
} from "@/helpers/notifications";
import "./AddOrEditModal.css";
import { PublicationApi } from "@/api/publication.api";
import { Issue, Publication } from "@/api/types";
import { getMonthYearDate } from "@/helpers/issuesAndSubscriptions";
import { useDisclosure } from "@mantine/hooks";
import AreYouSureModal from "@/components/Shared/AreYouSureModal/AreYouSureModal";

interface AddNewModalProps {
  type: "issue" | "publication";
  initialData?: Publication | Issue;
  opened: boolean;
  onClose: () => void;
}

const isIssue = (item: Issue | Publication): item is Issue => {
  return "month" in item && "year" in item;
};

interface FormValues {
  title: string;
  date?: Date | null;
  author?: string;
  price: string | null;
  description: string;
  cover: FileWithPath | null;
  pdf: FileWithPath | null;
}

const AddNewModal: React.FC<AddNewModalProps> = ({
  type,
  initialData,
  opened,
  onClose,
}) => {
  const typeIsIssue = type === "issue";
  const coverRef = useRef<() => void>(null);
  const pdfRef = useRef<() => void>(null);
  const [loading, setLoading] = useState(false);
  const [coverError, setCoverError] = useState("");
  const [pdfError, setPdfError] = useState("");
  const [coverLoading, setCoverLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const disableActions = coverLoading || pdfLoading;
  const [aysOpened, { open: aysOpen, close: aysClose }] = useDisclosure(false);
  const form = useForm<FormValues>({
    mode: "controlled",
    initialValues: {
      title: "",
      date: null,
      author: "",
      price: "",
      description: "",
      cover: null,
      pdf: null,
    },

    validate: {
      title: isNotEmpty("Naslov ne može biti prazan"),
      date: typeIsIssue ? isNotEmpty("Datum ne može biti prazan") : undefined,
      author: !typeIsIssue
        ? isNotEmpty("Autor ne može biti prazan")
        : undefined,
      price: isNotEmpty("Cijena ne može biti prazna"),
      description: isNotEmpty("Opis ne može biti prazan"),
      cover: isNotEmpty("Naslovnica nije dodana"),
      pdf: isNotEmpty("Sadržaj nije dodan"),
    },
  });

  useEffect(() => {
    const setInitialValues = async () => {
      if (!initialData) return;
      setCoverLoading(true);
      setPdfLoading(true);

      const isIssueItem = isIssue(initialData);

      const values: Partial<FormValues> = {
        title: initialData.title,
        date:
          isIssueItem && initialData.month && initialData.year
            ? getMonthYearDate(initialData.month, initialData.year)
            : null,
        author: !isIssueItem ? initialData.author ?? "" : "",
        price: initialData.price,
        description: initialData.description,
        cover: null,
        pdf: null,
      };
      form.setValues(values);

      const coverFile = await FileApi.getFile(initialData.cover.url);
      if (coverFile) {
        const file = new File([coverFile], initialData.cover.name, {
          type: coverFile.type,
        });
        form.setFieldValue("cover", file);
      }
      setCoverLoading(false);

      const pdfFile = await FileApi.getFile(initialData.pdf.url);
      if (pdfFile) {
        const file = new File([pdfFile], initialData.pdf.name, {
          type: pdfFile.type,
        });
        form.setFieldValue("pdf", file);
      }
      setPdfLoading(false);
    };

    if (opened && initialData) {
      setInitialValues();
    }
  }, [opened, initialData]);

  const handlePdfUpload = (files: FileWithPath[]) => {
    if (files.length > 0) {
      const file = files[0];
      form.setFieldValue("pdf", file);
      setPdfError("");
    }
  };

  const handleImageUpload = (files: FileWithPath[]) => {
    if (files.length > 0) {
      const file = files[0];
      form.setFieldValue("cover", file);
      setCoverError("");
    }
  };

  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true);
    const pdfLink = (await FileApi.linkRequest(values.pdf, "pdf")) ?? "";
    const coverLink = (await FileApi.linkRequest(values.cover, "cover")) ?? "";

    if (typeIsIssue) {
      const month = dayjs(values.date).format("M");
      const year = dayjs(values.date).format("YYYY");

      const newIssueData = {
        title: values.title,
        month: month,
        year: year,
        price: parseFloat(values.price ?? "0").toFixed(2),
        description: values.description,
        cover: { name: values.cover!.name, url: coverLink },
        pdf: { name: values.pdf!.name, url: pdfLink },
      };

      let response = null;
      if (initialData) {
        response = await IssueApi.editIssue(initialData.id, newIssueData);
      } else {
        response = await IssueApi.createNewIssue(newIssueData);
      }
      if (response?.isSuccessful) {
        successNotification({ message: "Revija uspješno spremljena" });
      } else {
        errorNotification({ message: "Molimo pokušajte ponovno!" });
      }
    } else {
      const newPublicationData = {
        title: values.title,
        author: values.author as string,
        price: parseFloat(values.price ?? "0").toFixed(2),
        description: values.description,
        cover: { name: values.cover!.name, url: coverLink },
        pdf: { name: values.pdf!.name, url: pdfLink },
      };

      let response = null;
      if (initialData) {
        response = await PublicationApi.editPublication(
          initialData.id,
          newPublicationData
        );
      } else {
        response = await PublicationApi.createNewPublication(
          newPublicationData
        );
      }
      if (response?.isSuccessful) {
        successNotification({ message: "Publikacija uspješno spremljena" });
      } else {
        errorNotification({ message: "Molimo pokušajte ponovno!" });
      }
    }

    setLoading(false);
    closeModal();
  };

  const closeModal = () => {
    if (disableActions) return;
    form.reset();
    onClose();
  };

  const handleErrors = (validationErrors: any) => {
    if (validationErrors) {
      if (validationErrors.cover) setCoverError(validationErrors.cover);
      if (validationErrors.pdf) setPdfError(validationErrors.pdf);
    }
  };

  const deleteItem = async () => {
    aysClose();
    let result;
    if (typeIsIssue) {
      result = await IssueApi.deleteIssue(initialData!.id);
    } else {
      result = await PublicationApi.deletePublication(initialData!.id);
    }
    if (result?.isSuccessful) {
      successNotification({
        message: result.message,
      });
      onClose();
    } else {
      errorNotification({
        message: result.message,
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title={`${!!initialData ? "Uredi" : "Dodaj"} ${
        typeIsIssue ? "reviju" : "publikaciju"
      }`}
      centered
      size="xl"
      classNames={{
        inner: "add-magazine-inner",
        content: "add-magazine-content",
        body: "add-magazine-body",
        header: "add-magazine-header",
        title: "add-magazine-title",
      }}
      closeButtonProps={{
        disabled: disableActions,
      }}
      trapFocus={false}
    >
      <form onSubmit={form.onSubmit(handleSubmit, handleErrors)}>
        <Stack gap="1.25rem" className="py-6 px-5">
          <div className="input-container">
            <TextInput
              label={`Naslov ${typeIsIssue ? "revije" : "publikacije"}*`}
              placeholder="Naslov"
              key={form.key("title")}
              {...form.getInputProps("title")}
              className="flex-1"
            />
            {typeIsIssue ? (
              <MonthPickerInput
                locale="hr"
                label="Datum izdavanja*"
                placeholder="MM/GGGG"
                valueFormat="MM/YYYY"
                key={form.key("date")}
                {...form.getInputProps("date")}
                className="flex-1"
                disabled={!!initialData}
              />
            ) : (
              <TextInput
                label="Autor(i)*"
                placeholder="Ime Prezime, Ime Prezime"
                key={form.key("author")}
                {...form.getInputProps("author")}
                className="flex-1"
              />
            )}
            <NumberInput
              label="Cijena (€)*"
              placeholder="Cijena"
              hideControls
              valueIsNumericString={true}
              key={form.key("price")}
              {...form.getInputProps("price")}
              className="flex-1"
              decimalScale={2}
            />
          </div>
          <Group>
            <Textarea
              label={`Opis ${typeIsIssue ? "revije" : "publikacije"}*`}
              placeholder="Opis"
              key={form.key("description")}
              {...form.getInputProps("description")}
              className="w-full"
              classNames={{ input: "!h-[100px]" }}
            />
          </Group>
          <div className="input-container">
            <Stack gap="0.5rem" className="flex-1">
              <div className="text-[#0b1a29] text-sm font-medium">
                Upload naslovnice*
              </div>
              {coverLoading ? (
                <div className="pl-10 pt-5">
                  <Loader size="sm" color="#004a99" />
                </div>
              ) : !form.values.cover ? (
                <>
                  <Dropzone
                    openRef={coverRef}
                    onDrop={handleImageUpload}
                    onReject={(files) => console.log("rejected files", files)}
                    maxSize={5 * 1024 ** 2}
                    key={form.key("cover")}
                    {...form.getInputProps("cover")}
                    accept={IMAGE_MIME_TYPE}
                    classNames={{ root: "!rounded-lg" }}
                  >
                    <div className="dropzone-group">
                      <UploadIcon />
                      <div className="text-[#b7b7b7] text-sm font-medium">
                        Povucite ili odaberite datoteku
                      </div>
                    </div>
                  </Dropzone>
                  {coverError && (
                    <span className="text-[#fa5252] text-xs">{coverError}</span>
                  )}
                </>
              ) : (
                <Group
                  justify="center"
                  mih={80}
                  gap={10}
                  className="cover-group"
                >
                  <Group gap={10}>
                    <Image
                      key={form.values.cover.name}
                      src={URL.createObjectURL(form.values.cover)}
                      className="w-16 h-[90px] !rounded"
                    />
                    <div className="text-[#0b1a29] text-sm font-bold break-all">
                      {form.values.cover.name}
                    </div>
                  </Group>
                  <Group gap={5}>
                    <ActionIcon
                      variant="transparent"
                      className="hover:opacity-80"
                      onClick={() => form.setFieldValue("cover", null)}
                    >
                      <DeleteIcon color="#0b1a29" />
                    </ActionIcon>
                    <ActionIcon
                      variant="transparent"
                      className="hover:opacity-80"
                      onClick={() => coverRef.current?.()}
                      style={{ pointerEvents: "all" }}
                    >
                      <EditIcon />
                    </ActionIcon>
                  </Group>
                </Group>
              )}
            </Stack>
            <Stack gap="0.5rem" className="flex-1">
              <div className="text-[#0b1a29] text-sm font-medium">
                Upload sadržaja*
              </div>
              {pdfLoading ? (
                <div className="pl-10 pt-5">
                  <Loader size="sm" color="#004a99" />
                </div>
              ) : !form.values.pdf ? (
                <>
                  <Dropzone
                    openRef={pdfRef}
                    onDrop={handlePdfUpload}
                    onReject={(files) => console.log("rejected files", files)}
                    maxSize={10 * 1024 ** 2}
                    key={form.key("pdf")}
                    {...form.getInputProps("pdf")}
                    accept={PDF_MIME_TYPE}
                    classNames={{ root: "!rounded-lg" }}
                  >
                    <div className="dropzone-group">
                      <UploadIcon />
                      <div className="text-[#b7b7b7] text-sm font-medium">
                        Povucite ili odaberite PDF datoteku
                      </div>
                    </div>
                  </Dropzone>
                  {pdfError && (
                    <span className="text-[#fa5252] text-xs">{pdfError}</span>
                  )}
                </>
              ) : (
                <div className="flex h-full items-center">
                  <Group
                    justify="center"
                    mih={80}
                    gap={10}
                    className="pdf-group"
                  >
                    <Group gap={10}>
                      <FileIcon />
                      <div className="text-[#0b1a29] text-sm font-bold break-all">
                        {form.values.pdf.name}
                      </div>
                    </Group>
                    <Group gap={5}>
                      <ActionIcon
                        variant="transparent"
                        className="hover:opacity-80"
                        onClick={() => form.setFieldValue("pdf", null)}
                      >
                        <DeleteIcon color="#0b1a29" />
                      </ActionIcon>
                      <ActionIcon
                        variant="transparent"
                        className="hover:opacity-80"
                        onClick={() => pdfRef.current?.()}
                        style={{ pointerEvents: "all" }}
                      >
                        <EditIcon />
                      </ActionIcon>
                    </Group>
                  </Group>
                </div>
              )}
            </Stack>
          </div>
        </Stack>
        <Group
          justify={initialData ? "space-between" : "end"}
          className="border-t border-[#e6e6e6] !p-4"
        >
          {initialData && (
            <Button
              variant="transparent"
              leftSection={<DeleteIcon />}
              className="text-button delete-item-button"
              disabled={disableActions}
              onClick={aysOpen}
            >
              {`Obriši ${typeIsIssue ? "reviju" : "publikaciju"}`}
            </Button>
          )}
          <div className="modal-button-group">
            <Button
              variant="outline"
              color="#515151"
              radius="xl"
              size="md"
              className="modal-button"
              disabled={disableActions}
              onClick={closeModal}
            >
              Odustani
            </Button>
            <Button
              type="submit"
              variant="filled"
              color="#004a99"
              radius="xl"
              size="md"
              className="modal-button"
              disabled={disableActions}
              loading={loading}
            >
              Spremi
            </Button>
          </div>
        </Group>
      </form>
      <AreYouSureModal
        opened={aysOpened}
        message={`Jeste li sigurni da želite obrisati ${
          typeIsIssue ? "reviju" : "publikaciju"
        }?`}
        warning={`*${typeIsIssue ? "Revije" : "Publikacije"} koje su ${
          typeIsIssue ? "pojedinačno" : ""
        } kupljene ne mogu biti obrisane!`}
        onClose={aysClose}
        onAction={deleteItem}
      />
    </Modal>
  );
};

export default AddNewModal;
